/**
 * Інвентаризація розрахунків — список (канон Норми #29, 2026-08-22):
 * .card + InfiniteDocGrid (серверна пагінація/сортування), фільтр
 * організації рядком під назвою з опцією «Усі організації» (пусто =
 * organization_id не передається, сервер віддає всі доступні юрособи).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { orgs } from '@/api/catalogs';
import { settlementInventories, type SettlementInventory } from '@/api/settlements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'documents.settlement_inventories.create';

const STATE_LABEL: Record<string, string> = {
  IN_PROGRESS: 'У процесі', COMPLETED: 'Завершено',
};

export default function SettlementInventoriesPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)), [perms.availableOrgs],
  );
  // Пусто = «Усі організації» (за замовчуванням).
  const [orgId, setOrgId] = useState<string>('');
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgList.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgList.data]);

  const { datasource, total } = useInfiniteGridDatasource<SettlementInventory>(
    (limit, offset, sort) => settlementInventories.list({
      organization_id: orgId || undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId],
  );

  const cols = useMemo<ColDef<SettlementInventory>[]>(() => [
    {
      field: 'document_number', headerName: 'Номер', width: 140,
      cellRenderer: (p: { data?: SettlementInventory }) => p.data ? (
        <span>
          {p.data.external_source && (
            <span title="Імпортовано з 1С" style={{
              marginRight: 6, color: '#2563eb', border: '1px solid var(--badge-blue-bd)',
              borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px',
            }}>I</span>
          )}
          {p.data.document_number}
        </span>
      ) : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 110,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 190,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    { field: 'inventory_date', headerName: 'Дата інвент.', width: 130 },
    {
      field: 'total_book_amount', headerName: 'Книжкова сума', width: 150,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'total_confirmed_amount', headerName: 'Підтверджено', width: 150,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'total_variance_amount', headerName: 'Розбіжність', width: 150,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'state', headerName: 'Стан', width: 130,
      valueFormatter: (p) => p.value ? (STATE_LABEL[p.value] ?? p.value) : '',
    },
  ], [orgName]);

  // «+ Інвентаризація» при «Усі організації» — у дефолтну доступну юрособу.
  const createOrgId = orgId || pickDefaultOrg(orgList.data?.items)?.id || '';
  const canCreate = !!createOrgId && allowedOrgIds.has(createOrgId);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Інвентаризація розрахунків<TitleTotal total={total} /></h2>
        <button className="primary" disabled={!canCreate} style={{ marginLeft: 'auto' }}
          onClick={() => navigate(`/settlements/settlement-inventories/new?org=${createOrgId}`)}>
          + Інвентаризація
        </button>
      </div>
      <div className="filters">
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 200, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
      </div>
      <InfiniteDocGrid<SettlementInventory>
        datasource={datasource}
        columnDefs={cols}
        storageKey="settlement-inventories"
        onRowDoubleClicked={(r) => navigate(`/settlements/settlement-inventories/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
